// Unified "System variable" picker for a criterion's compare value.
//
// One vocabulary everywhere: the same token families the DEFAULT-VALUE editor
// offers (CriteriaDefaultField) are available as a compare variable —
//   • Dates          — the server ?.@ calendar tokens (Today, 1stDayOfMonth…)
//   • Current user   — ?.@ LoginUser / User_Email plus the ':user_employee' /
//                      ':user_site' value tokens
//   • Project vars   — ':<name>' tokens (fixed-, SQL- or prompt-backed)
// '@'-style tokens resolve via bi_sql's system resolver; ':' tokens via the
// engine's visual_param_tokens (the backend criteria path falls back to it —
// visual_data_service._with_value_token_fallback).

import { useMemo } from 'react'
import { projectVarToken } from '@/livepreview/utils'
import type { ProjectVariable } from '@/api/projectVariables'
import { SYSTEM_VARIABLES } from './systemVariables'
import { USER_INFO_TOKENS } from './CriteriaDefaultField'

// ':user_*' tokens NOT already covered by the ?.@ user tokens.
const EXTRA_USER_TOKENS = USER_INFO_TOKENS.filter(
  (u) => u.token === ':user_employee' || u.token === ':user_site',
)

interface Props {
  value: string
  projectVars: ProjectVariable[]
  onChange: (token: string) => void
  selectStyle?: React.CSSProperties
}

export default function VariableTokenSelect({
  value, projectVars, onChange, selectStyle,
}: Props) {
  const v = (value || '').trim()
  const known = useMemo(() => {
    const s = new Set<string>()
    for (const t of SYSTEM_VARIABLES) s.add(t.token)
    for (const t of EXTRA_USER_TOKENS) s.add(t.token)
    for (const pv of projectVars) s.add(projectVarToken(pv.name))
    return s
  }, [projectVars])

  return (
    <>
      <select
        value={v}
        onChange={(e) => onChange(e.target.value)}
        style={selectStyle}
        title="System variable (resolved on the server at run time)"
      >
        <optgroup label="Dates">
          {SYSTEM_VARIABLES.filter((s) => s.group === 'Dates').map((s) => (
            <option key={s.token} value={s.token}>{s.label}</option>
          ))}
        </optgroup>
        <optgroup label="Current user">
          {SYSTEM_VARIABLES.filter((s) => s.group === 'Current user').map((s) => (
            <option key={s.token} value={s.token}>{s.label}</option>
          ))}
          {EXTRA_USER_TOKENS.map((u) => (
            <option key={u.token} value={u.token}>{u.label}</option>
          ))}
        </optgroup>
        {projectVars.length > 0 ? (
          <optgroup label="Project variables">
            {projectVars.map((pv) => (
              <option key={pv.name} value={projectVarToken(pv.name)}>
                {pv.description || pv.name}{pv.kind === 'sql' ? ' (SQL)' : ''}
              </option>
            ))}
          </optgroup>
        ) : (
          <optgroup label="Project variables">
            <option value="" disabled>(none defined — add them in Project settings)</option>
          </optgroup>
        )}
        {v && !known.has(v) && <option value={v}>{v}</option>}
      </select>
      <div style={{
        fontFamily: "var(--font-mono, 'IBM Plex Mono', ui-monospace, monospace)",
        fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)', padding: '4px 8px',
        border: '1px dashed var(--color-border)', borderRadius: 4, marginTop: 6,
      }}>
        {v ? (v.startsWith(':') ? v : `?.@${v}`) : '…'}{' '}
        <span style={{ fontStyle: 'italic' }}>· resolved at run time, never prompts</span>
      </div>
    </>
  )
}
